/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FADE_UP_SM } from '@/lib/motionPresets';
import { toast } from 'sonner';
import { Palette, Save } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getBrandSettings, updateBrandSettings } from '@/lib/brandSettings';
import { useAuth } from '@/lib/auth';
import { useBrand } from '@/lib/whitelabel';
import LogoUploader from '@/components/admin/LogoUploader';
import BrandLivePreview from '@/components/admin/BrandLivePreview';
import { BrandPalettesCard } from '@/components/brand/BrandPalettesCard';
import { BrandColorsCard } from '@/components/brand/BrandColorsCard';
import { IseeTiersEditor, withTierKeys, serializeTiers } from '@/components/brand/IseeTiersEditor';

export default function BrandSettingsPage() {
  const { user } = useAuth() || {};
  const brand = useBrand();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const s = await getBrandSettings();
        setSettings({ ...s, iseeTiers: withTierKeys(s.iseeTiers) });
      } catch (e) {
        toast.error('Impossibile caricare le impostazioni brand');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patch = (obj) => setSettings((s) => ({ ...s, ...obj }));

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const payload = { ...settings };
      delete payload.id;
      delete payload.updated_at;
      delete payload.updated_by;
      // Ensure numeric fields are numbers and strip UI-only keys.
      payload.iseeTiers = serializeTiers(payload.iseeTiers);
      const updated = await updateBrandSettings(payload);
      setSettings({ ...updated, iseeTiers: withTierKeys(updated.iseeTiers) });
      // Applica subito palette, logo e nome a tutta l'app (nessun reload necessario).
      await brand.reload?.();
      toast.success('Impostazioni brand salvate e applicate.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (user && !user.is_admin) {
    return (
      <div className="p-6">
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          Accesso riservato agli amministratori.
        </CardContent></Card>
      </div>
    );
  }

  if (loading || !settings) {
    return <div className="p-6 text-sm text-muted-foreground">Caricamento...</div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl" data-testid="brand-settings-page">
      <motion.div initial={FADE_UP_SM.initial} animate={FADE_UP_SM.animate} className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Palette className="h-7 w-7 text-primary" /> Personalizzazione brand
          </h1>
          <p className="text-sm text-muted-foreground">
            Modifica logo, colori, nome clinica, dati legali e fasce ISEE senza toccare il codice.
            Le modifiche vengono applicate a tutti gli utenti al prossimo caricamento pagina.
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="brand-save-btn">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Salvataggio...' : 'Salva'}
        </Button>
      </motion.div>

      <div className="grid gap-4 lg:grid-cols-[1fr_360px] items-start">
        <div className="space-y-4 min-w-0">

      <Card>
        <CardHeader><CardTitle className="text-base">Identità clinica</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nome clinica</Label>
            <Input value={settings.clinicName || ''} onChange={(e) => patch({ clinicName: e.target.value })} data-testid="brand-clinic-name" />
          </div>
          <div>
            <Label>Nome breve</Label>
            <Input value={settings.clinicShortName || ''} onChange={(e) => patch({ clinicShortName: e.target.value })} data-testid="brand-clinic-short" />
          </div>
          <div className="md:col-span-2">
            <Label>Tagline / slogan</Label>
            <Input value={settings.tagline || ''} onChange={(e) => patch({ tagline: e.target.value })} data-testid="brand-tagline" />
          </div>
          <div className="md:col-span-2">
            <LogoUploader
              currentUrl={settings.logoUrl}
              onUploaded={(updated) => setSettings((s) => ({ ...s, ...updated }))}
            />
          </div>
          <div className="md:col-span-2">
            <Label>URL favicon</Label>
            <Input value={settings.faviconUrl || ''} onChange={(e) => patch({ faviconUrl: e.target.value })} placeholder="https://…/favicon.png" data-testid="brand-favicon" />
          </div>
        </CardContent>
      </Card>

      <BrandPalettesCard settings={settings} patch={patch} />

      <BrandColorsCard settings={settings} patch={patch} />

      <Card>
        <CardHeader><CardTitle className="text-base">Dati legali (per fatture PDF/XML e pagine /legal)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Ragione sociale</Label>
            <Input value={settings.legalOwner || ''} onChange={(e) => patch({ legalOwner: e.target.value })} data-testid="brand-legal-owner" />
          </div>
          <div>
            <Label>P.IVA / C.F.</Label>
            <Input value={settings.legalVat || ''} onChange={(e) => patch({ legalVat: e.target.value })} data-testid="brand-legal-vat" />
          </div>
          <div>
            <Label>Email DPO</Label>
            <Input type="email" value={settings.legalDpo || ''} onChange={(e) => patch({ legalDpo: e.target.value })} data-testid="brand-legal-dpo" />
          </div>
          <div className="md:col-span-2">
            <Label>Sede legale</Label>
            <Textarea rows={2} value={settings.legalAddress || ''} onChange={(e) => patch({ legalAddress: e.target.value })} data-testid="brand-legal-address" />
          </div>
          <div>
            <Label>Email contatto pubblico</Label>
            <Input type="email" value={settings.contactEmail || ''} onChange={(e) => patch({ contactEmail: e.target.value })} data-testid="brand-contact-email" />
          </div>
          <div>
            <Label>Telefono contatto</Label>
            <Input value={settings.contactPhone || ''} onChange={(e) => patch({ contactPhone: e.target.value })} data-testid="brand-contact-phone" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Fasce ISEE (scala mobile)</CardTitle></CardHeader>
        <CardContent>
          <IseeTiersEditor tiers={settings.iseeTiers || []} onChange={(tiers) => patch({ iseeTiers: tiers })} testIdPrefix="brand-tier" />
          <p className="text-xs text-muted-foreground mt-2">
            La fascia con <b>Soglia vuota</b> è quella “over threshold” (fuori scala) e viene applicata a chi supera tutte le altre.
          </p>
        </CardContent>
      </Card>

      {settings.updated_at && (
        <p className="text-xs text-muted-foreground text-right" data-testid="brand-updated-at">
          Ultimo aggiornamento: {new Date(settings.updated_at).toLocaleString('it-IT')}
        </p>
      )}
        </div>

        {/* Sticky Live Preview panel */}
        <aside className="lg:min-h-full">
          <BrandLivePreview settings={settings} />
        </aside>
      </div>
    </div>
  );
}
